<template>
    <div v-if="device">

        <loading-state
            fullscreen
            v-if="loading">
        </loading-state>

        <!--        <job-complete-nav></job-complete-nav>-->

        <div class="container repair-container job-complete">

            <h2 v-if="!device.review_complete">Step 1: Review the repair</h2>

            <div id="complete" class="card card-body pt-3 pb-4" v-if="device.review_complete">

                <!-- If the job type is not an asset -->
                <template v-if="!isRefurbOrMailIn">
                    <!-- Review Completed -->
                    <empty-state
                        v-if="!device.complete && deviceStatus"
                        icon="checklist"
                        title="Review completed"
                        :subtitle="`Repair status: ${deviceStatus.title}`">
                    </empty-state>

                    <!-- Repair Completed -->
                    <empty-state
                        v-if="device.complete"
                        statusIcon="success"
                        title="This repair has been completed">
                    </empty-state>
                </template>

                <!-- If the job type is an asset -->
                <template v-if="isRefurbOrMailIn">
                    <!-- Completed -->
                    <empty-state
                        v-if="device.complete"
                        icon="repair-kits-packing"
                        title="This repair has been completed"
                        subtitle="Please hand the device over to the returns team">
                    </empty-state>

                    <!-- Device Beyond Economic Repair -->
                    <empty-state
                        v-if="deviceStatus && deviceStatus.title === 'Device Beyond Repair'"
                        icon="ber"
                        title="Device Beyond Economic Repair"
                        subtitle="Please hand the device over to the returns team">
                    </empty-state>
                </template>

                <div class="row">
                    <div class="col-md-5 offset-md-1">
                        <button class="btn btn-block btn-outline-info mb-2 px-5" v-b-toggle.progress-step-list @click="scrollToProgressSteps()">Make changes</button>
                    </div>
                    <!--                    <div class="col-md-5 offset-md-1">-->
                    <!--                        <button class="btn btn-block btn-info mb-2 px-5" v-b-toggle.progress-step-list @click="scrollToProgressSteps()">Make changes</button>-->
                    <!--                    </div>-->
                    <div class="col-md-5 m-auto">
                        <router-link :to="{ name: 'job'}" class="btn btn-block btn-success mb-2 px-5">Back to job</router-link>
                    </div>
                </div>
            </div>

            <!-- Job Details -->
            <div id="job-details" class="card card-body pb-0">
                <h5>Job details</h5>
                <dl>
                    <dt>Order ID</dt>
                    <dd>{{ jobId }}</dd>
                </dl>
                <dl v-if="deviceStatus">
                    <dt>Status</dt>
                    <dd>{{ deviceStatus.title }}</dd>
                </dl>
                <!--                <dl class="mb-0">-->
                <!--                    <dt>Repair duration</dt>-->
                <!--                    <dd>Repair time: 10:32 - 11:21</dd>-->
                <!--                    <dd>Duration: 49m</dd>-->
                <!--                </dl>-->
            </div>

            <!-- Device Details -->
            <div id="device-details" class="card card-body pb-0">
                <h5>Device</h5>
                <div class="media mb-4">
                    <div class="media-left">
                        <img v-if="device.version_image" :src="device.version_image" class="img-2x">
                        <template v-if="!device.version_image">
                            <icon-mobile v-if="device.type === 'phone'" size="2x" class="icon-all-muted"></icon-mobile>
                            <icon-tablet v-if="device.type === 'tablet'" size="2x" class="icon-all-muted"></icon-tablet>
                        </template>
                    </div>
                    <div class="media-body">
                        <h5 class="mb-2">{{ device.title_with_colour }}</h5>
                        <p class="mb-3 text-muted text-sm">IMEI: {{ device.imei }}</p>
                        <ul class="list-unstyled text-muted text-sm">
                            <li v-for="repair in deviceRepairs" :key="repair.id">
                                {{repair.title}}
                            </li>
                        </ul>
                    </div>
                </div>
                <template v-if="deviceNotes.length > 0">
                    <button class="btn btn-outline-light mb-4" v-b-toggle.device-notes-collapse>Device Notes ({{ deviceNotes.length }})</button>
                    <b-collapse id="device-notes-collapse">
                        <note-item :jobNotes="deviceNotes" small></note-item>
                    </b-collapse>
                </template>
            </div>

            <!-- Repair Images -->
            <div id="repair-images" class="card card-body pb-0">
                <h5>Repair photos</h5>
                <div class="card-grid grid-col-2 grid-col-sm-3 grid-col-md-6">
                    <image-item
                        v-for="(imagePath, key) in imagePaths" :key="key"
                        :imageType="imagePath.type"
                        :imageData="imagePath.path"
                        small>
                    </image-item>
                </div>
                <div class="row">
                    <div class="col-6 pr-2">
                        <router-link :to="{name: 'repair.beforePictures'}" class="btn btn-block btn-info btn-sm mb-4 px-1">Add before photos</router-link>
                    </div>
                    <div class="col-6 pl-2">
                        <router-link :to="{name: 'repair.afterPictures'}" class="btn btn-block btn-info btn-sm mb-4 px-1">Add after photos</router-link>
                    </div>
                </div>
            </div>

            <!-- IRIS & Symptom Codes -->
            <div id="iris-symptom-codes" class="card card-body pb-0" v-if="device.make_id === '2'">
                <h5>Symptom &amp; IRIS codes</h5>
                <dl>
                    <dt>Symptom codes</dt>
                    <dd v-if="symptomCodeOne"><span class="font-weight-normal">Code One:</span> {{ symptomCodeOne.title }}</dd>
                    <dd v-if="symptomCodeTwo"><span class="font-weight-normal">Code Two:</span> {{ symptomCodeTwo.title }}</dd>
                    <dd v-if="symptomCodeThree"><span class="font-weight-normal">Code Three:</span> {{ symptomCodeThree.title }}</dd>
                </dl>
                <router-link :to="{name: 'repair.symptomCodes'}" class="btn btn-info btn-sm mb-4 align-self-start">Edit symptom codes</router-link>
                <dl>
                    <dt>IRIS codes</dt>
                    <dd v-if="irisCodeOne"><span class="font-weight-normal">Code One:</span> {{ irisCodeOne.description }}</dd>
                    <dd v-if="irisCodeTwo"><span class="font-weight-normal">Code Two:</span> {{ irisCodeTwo.description }}</dd>
                    <dd v-if="irisCodeThree"><span class="font-weight-normal">Code Three:</span> {{ irisCodeThree.description }}</dd>
                    <dd v-if="irisCodeFour"><span class="font-weight-normal">Code Four:</span> {{ irisCodeFour.description }}</dd>
                </dl>
                <router-link :to="{name: 'repair.irisCodes'}" class="btn btn-info btn-sm mb-4 align-self-start">Edit IRIS codes</router-link>
            </div>

            <!-- Confirmed Problems -->
            <div id="confirmed-problems" class="card card-body pb-0" v-if="confirmedProblems.length > 0">
                <h5>Confirmed problems</h5>
                <dl>
                    <dt>Problems</dt>
                    <dd v-for="problem in confirmedProblems" :key="problem.id">
                        {{ problem.title }}
                    </dd>
                </dl>
                <router-link :to="{name: 'repair.confirmProblems'}" class="btn btn-info btn-sm mb-4 align-self-start">Edit problems</router-link>
            </div>

            <!-- Parts Used -->
            <div id="parts-used" class="card card-body pb-0">
                <h5>Parts used</h5>
                <empty-state
                    align="left"
                    faIcon="microchip"
                    inline
                    small
                    title="No parts were used"
                    :subtitle="device.no_parts_used_reason"
                    v-if="deviceParts.length === 0 && devicePartsFaulty.length === 0">
                </empty-state>

                <dl v-if="deviceParts.length > 0">
                    <dt>Parts used</dt>
                    <dd v-for="part in deviceParts" :key="part.id">
                        {{ part.stock_item_title }}
                    </dd>
                </dl>

                <dl v-if="devicePartsFaulty.length > 0">
                    <dt>Faulty parts</dt>
                    <dd v-for="part in devicePartsFaulty" :key="part.id">
                        {{ part.stock_item_title }}
                    </dd>
                </dl>

                <button @click="editPartsUsed" class="btn btn-info btn-sm mb-4 align-self-start">Edit parts</button>
            </div>

            <!-- Complete Description -->
            <div id="parts-used" class="card card-body pb-0">
                <h5>Completed Repair Description</h5>
                <p>
                    {{ this.device.complete_description }}
                </p>
            </div>

            <div>
                <h2 v-if="!device.review_complete">Step 2: Select status &amp; complete repair</h2>
                <!-- Status select -->
                <div id="status-select" class="card card-body" v-if="!device.review_complete">
                    <template v-if="!loadingRevisit">
                        <div class="form-group mb-4">
                            <h5>Select the status</h5>
                            <select class="custom-select" v-model="selectedStatus" @change="handleDeviceStatusSelectChange">
                                <!--                                <optgroup v-if="job.job_type === 'refurbishment'" label="Completed">-->
                                <!--                                    <option value="37">Repaired Pending QC</option>-->
                                <!--                                </optgroup>-->
                                <!--                                <optgroup v-else label="Completed">-->
                                <optgroup label="Completed">
                                    <option value="1">Completed</option>
                                </optgroup>
                                <optgroup label="Other statuses">
                                    <option
                                        v-for="status in filteredDeviceStatuses"
                                        :key="status.key"
                                        :value="status.id">
                                        {{ status.title }}
                                    </option>
                                </optgroup>
                            </select>
                        </div>

                        <div class="alert alert-warning alert-centered rounded-0 mb-4 text-sm" v-if="selectedStatusSendsReplacement">
                            <animated-warning></animated-warning> Selecting this status will result in Likewize sending a replacement device to the customer.<br/>Are you sure this is correct?
                        </div>

                        <div v-if="subStatusIsRequired" class="form-group">
                            <h5>Please specify</h5>
                            <select class="custom-select" v-model="selectedSubStatus">
                                <option :value="null">Open this select menu</option>
                                <option
                                    v-for="status in filteredDeviceSubStatuses"
                                    :key="status.key"
                                    :value="status.id">
                                    {{ status.title }}
                                </option>
                            </select>
                        </div>

                        <template v-if="soErrors.length !== 0">
                            <h5>There have been some errors on this service order</h5>
                            <p v-for="(error, index) in soErrors" :key="index">{{error}}</p>
                        </template>

                        <!-- Revisit not required -->
                        <!--                    <template v-if="!revisitRequired">-->
                        <button class="btn btn-success btn-lg mx-auto px-5" v-if="serviceOrderRequired && !revisitRequired" @click="completeServiceOrder">Complete Service Order<template v-if="device.so_number">: {{ device.so_number }}</template></button>

                        <button class="btn btn-lg btn-success mx-auto px-5" v-if="!serviceOrderRequired" @click="checkInWarrantyRequired()" :disabled="!selectedStatus || serviceOrderRequired || (subStatusIsRequired && !subStatusHasBeenSelected)">
                            <template v-if="!selectedStatus">Select a status</template>
                            <template v-if="selectedStatus && selectedStatus !== '22'">Confirm status and finish repair</template>
                            <template v-if="selectedStatus && selectedStatus === '22'">Confirm status and raise SAW</template>
                        </button>
                        <!--                    </template>-->

                        <!-- Revisit bits -->
                        <template v-if="!isRefurbOrMailIn">
                            <template v-if="revisitRequired && isOnline">
                                <h2 class="mt-4">Revisit Required</h2>
                                <p>A revisit booking is required for this status change, either click below to book a revisit or change the status.</p>
                                <button type="button" class="btn btn-danger btn-lg mx-auto px-5" @click="bookRevisitLoadJob()">Book Revisit</button>
                            </template>

                            <template v-if="revisitRequired && isOffline">
                                <h2 class="mt-4">Revisit Required</h2>
                                <p>A revisit is required for this order, however you are currently offline.</p>
                            </template>

                            <!--                            <template v-if="revisitRequired && isSelfServe">-->
                            <!--                                <h2 class="mt-4">Revisit Required</h2>-->
                            <!--                                <p>A revisit is required to complete this job, as the customer is not available click the button below to send them a self serve revisit booking form.</p>-->
                            <!--                                <button type="button" class="btn btn-danger btn-lg mx-auto px-5" @click="bookRevisitSelfServe()">Book Revisit</button>-->
                            <!--                            </template>-->
                        </template>

                        <template v-if="isRefurbOrMailIn && revisitRequired">
                            <div class="alert alert-info mb-0 mt-4">
                                <animated-info></animated-info>
                                <div class="alert-content">Please return this device to the office.</div>
                            </div>
                        </template>
                    </template>

                    <!-- Loading revisit -->
                    <empty-state
                        v-if="loadingRevisit"
                        class="bg-transparent"
                        statusIcon="loading"
                        title="Loading revisit booking"
                        subtitle="Please wait">
                    </empty-state>

                </div>
            </div>
            <!-- Footer -->
            <!--            <job-complete-footer class="active"></job-complete-footer>-->

        </div>

        <remove-parts-modal></remove-parts-modal>
        <defect-description-modal :deviceId="deviceId" @saved="finishServiceOrder"></defect-description-modal>
    </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex';

import '@firebase/storage';

export default {

    props:['jobId', 'deviceId'],

    data() {
        return {
            imagePaths:[],
            isLoadingImages: false,
            isSelfServe: false,
            loadingRevisit: false,
            loading: false,
            printing: false,
            revisitRequired: false,
            selectedStatus: null,
            serviceOrderRequired: false,
            showIrisSymptomCodes: false,
            soErrors: [],
            selectedSubStatus: null,
            syncInProgress: false,
        }
    },

    computed: {
        ...mapGetters([
            "confirmedProblems",
            "device",
            "deviceAssets",
            "deviceNotes",
            "devicePartsFaulty",
            "deviceParts",
            "deviceRepairs",
            "deviceStatus",
            "deviceStatuses",
            "irisCodeOne",
            "irisCodeTwo",
            "irisCodeThree",
            "irisCodeFour",
            "isSuperAdmin",
            "job",
            "symptomCodeOne",
            "symptomCodeTwo",
            "symptomCodeThree",
            "isRefurbOrMailIn",
            "selectedDeviceStatusSubStatuses",
            "userService",
        ]),

        filteredDeviceStatuses() {
            return this.deviceStatuses.filter((el) => {
                return (
                    !el.admin_only &&
                    el.title &&
                    (
                        el.title !== "Completed" &&
                        el.title !== "Repaired Pending QC" &&
                        el.title !== "Cancelled Repair"
                    )
                )
            });
        },

        filteredDeviceSubStatuses() {
            return this.selectedDeviceStatusSubStatuses.filter((el) => {
                return (
                    !el.admin_only
                    // (
                    //     this.isSuperAdmin ||
                    //     this.isInHouseTechnician
                    // )
                )
            });
        },

        subStatusIsRequired() {
            return this.selectedDeviceStatusSubStatuses.length > 0;
        },

        selectedStatusSendsReplacement() {
            if (
                this.job.company_name !== 'Brightstar' &&
                this.job.company_name !== 'Brightstar Bulk' &&
                this.job.company_name !== 'Likewize' &&
                this.job.company_name !== 'Likewize Bulk'
            ) {
                return false;
            }

            for (let i = 0; i < this.filteredDeviceStatuses.length; i++) {
                if (this.filteredDeviceStatuses[i].id === this.selectedStatus) {
                    return this.filteredDeviceStatuses[i].sends_replacement;
                }
            }
            return false;
        },

        subStatusHasBeenSelected() {
            return this.selectedSubStatus !== null && this.selectedSubStatus !== "Please select a sub status";
        },

        shouldDisableUpdateStatusBtn() {
            if(this.subStatusIsRequired) {
                if(!this.subStatusHasBeenSelected) {
                    return true;
                }
            }
            return false;
        }
    },

    watch: {
        device(newDevice) {
            if (newDevice && newDevice.so_complete && !newDevice.so_error) {
                this.updateDeviceStatus();
            }

            if (newDevice && newDevice.so_complete && newDevice.so_error) {
                this.soErrors = JSON.parse(newDevice.so_errors);

                this.handleSoErrors();
            }
        },

        jobId(jobId) {
            if (jobId) {
                this.loadJob({
                    jobId: this.jobId
                });
            }
        }
    },

    mounted() {
        this.loading = false;
        this.loadDeviceForJob({
            jobId: this.jobId,
            deviceId: this.deviceId,
        }).then(() => {
            this.loadDeviceStatus({statusId: this.device.status_id});
        });
        this.loadDeviceAssets({
            jobId: this.jobId,
            deviceId:this.deviceId
        });
        this.listImages();
        this.loadAddedConfirmedProblems({
            jobId: this.jobId,
            deviceId: this.deviceId
        });
        this.loadDeviceNotes({
            jobId: this.jobId,
            deviceId: this.deviceId
        });
        this.loadDeviceRepairs({
            jobId: this.jobId,
            deviceId: this.deviceId
        });
        this.loadDeviceStatuses();
        this.loadFaultyParts({
            jobId:this.jobId,
            deviceId: this.deviceId
        });
        this.loadIrisCodesForDevice({
            jobId:this.jobId,
            deviceId:this.deviceId
        });
        this.loadParts({
            jobId:this.jobId,
            deviceId: this.deviceId
        });
        this.loadSymptomCodesForDevice({
            jobId:this.jobId,
            deviceId:this.deviceId
        });
        this.setCurrentStep({
            jobId: this.jobId,
            deviceId: this.deviceId,
            currentStep: this.$route.name,
        });
    },

    methods: {
        ...mapActions([
            "clearPartsUsedConfirmed",
            "displayToast",
            "loadAddedConfirmedProblems",
            "loadDeviceAssets",
            "loadDeviceForJob",
            "loadDeviceNotes",
            "loadFaultyParts",
            "loadJob",
            "loadParts",
            "loadDeviceRepairs",
            "loadDeviceStatus",
            "loadDeviceStatuses",
            "loadIrisCodesForDevice",
            "loadSymptomCodesForDevice",
            "printDpdLabel",
            "setCurrentStep",
            "setDeviceComplete",
            "setDeviceRepairedPendingQC",
            "setDeviceNotComplete",
            "setDeviceReviewComplete",
            "setDeviceStatus",
            "setPartsUsedConfirmed",
            "setNoPartsRequired",
            "loadDeviceSubStatuses",
            "syncServiceOrderWithGspn",
            "loadSoWarrantyErrors"
        ]),

        /** Revisit load job */
        bookRevisitLoadJob() {
            this.loadingRevisit = true;
            this.$http.get(`/api/v4/warranty/booking/build/${this.job.actual_order_id}`)
                .then(res => {
                    localStorage.revisitBookingId = res.data.data.booking_reference;
                    this.loadingRevisit = false;
                    this.$router.push(`/jobs/${this.jobId}/book-revisit`)
                });
        },

        /** Revisit self serve */
        bookRevisitSelfServe() {
            this.loadingRevisit = true;
            this.$http.post(`/api/v4/orders/${this.job.order_id}/revisit-invitation`, {
                "reason": "Sorry we missed you, click below to book a revisit."
            }).then(() => {
                this.loadingRevisit = false;
                this.$router.push('job');
            });
        },

        /** Check if in warranty repair */
        checkInWarrantyRequired() {
            if (this.device.so_number && this.device.so_number !== '' && this.selectedStatus === "1") {
                this.serviceOrderRequired = true;
            } else {
                this.updateDeviceStatus();
            }
        },

        /** Complete the service order */
        completeServiceOrder() {
            // GSPN rejects the completion without a defect description, so make sure we have one first
            if (!this.device.customer_fault_description || !this.device.customer_fault_description.trim()) {
                this.$bvModal.show('defect-description-modal');
                return;
            }

            this.finishServiceOrder();
        },

        /** Send the finish request, the device in the store can lag behind a just-saved description so this skips that check */
        finishServiceOrder() {
            this.loading = true;
            this.soErrors = [];
            this.$http.post(`/api/v4/orders/devices/${this.deviceId}/finish-so`)
                .then(response => {
                    if (response.data.data.errors && response.data.data.errors.length) {
                        this.soErrors = response.data.data.errors;
                        this.handleSoErrors();
                    }
                })
                .catch(error => {
                    this.loading = false;

                    if (error.response && error.response.data && error.response.data.code === 'defect_description_missing') {
                        this.$bvModal.show('defect-description-modal');
                    }
                });
        },

        /** Handle the SO errors that we've gotten back */
        handleSoErrors() {
            let update = false;

            for (let i = 0; i < this.soErrors.length; i++) {
                if (this.soErrors[i] && this.soErrors[i].includes("is already closed")) {
                    update = true;
                } else if (this.soErrors[i] && this.soErrors[i].includes("is already cancelled")) {
                    update = true;
                }
            }

            if (update) {
                this.updateDeviceStatus();
            } else {
                this.loading = false;
            }
        },

        /** Edit parts used */
        editPartsUsed() {
            this.clearPartsUsedConfirmed({
                jobId:this.jobId,
                deviceId:this.deviceId
            }).then(() => {
                this.$router.push({name: 'repair.addParts'});
            });
        },

      listImages() {
        this.isLoadingImages = true;

        this.$http.get(`/api/v4/images/${this.deviceId}`)
            .then((response) => {
              const items = response.data.data;

              const urls = items.map(item => {
                // Extract type from the title (e.g., "moh-after-back")
                const splitTitle = item.title.split("-");
                const type = splitTitle.length > 2 ? `${splitTitle[1]}-${splitTitle[2]}` : splitTitle[1] ?? "other";

                return {
                  path: item.path, // This is the actual image URL
                  type: type
                };
              });

              this.imagePaths = urls;
            })
            .catch(error => {
              console.error("Error fetching images:", error);
              this.displayToast({
                text: error?.response?.statusText ?? "Error fetching images",
                type: "error"
              });
            })
            .finally(() => {
              this.isLoadingImages = false;
            });
      },

        /** Print the DPD label */
        printTheDpdLabel() {
            if (this.deviceAssets.length) {
                this.printing = true;
                this.printDpdLabel(this.deviceAssets[0].id).then(() => {
                    this.displayToast({text: 'The label is printing.', type: 'success'});
                    this.printing = false;
                }).catch(e => {
                    this.displayToast({text: e, type: 'error'});
                    this.printing = false;
                })
            }
        },

        /** Scroll to the progress steps when clicking the edit button */
        scrollToProgressSteps() {
            this.$nextTick(() => {
                document.getElementById('progress-steps').scrollIntoView();
            })
        },

        /** Update the device status and set review as complete */
        updateDeviceStatus() {
            if (!this.selectedStatus) return;

            this.revisitRequired = false;
            this.isSelfServe = false;

            this.deviceStatuses.forEach(status => {
                if (status.id === this.selectedStatus) {
                    this.revisitRequired = status.revisit_required;

                    if (this.selectedStatus == 3 || this.selectedStatus == 4) {
                        this.isSelfServe = true;
                    }
                }
            });

            this.setDeviceStatus({
                jobId: this.jobId,
                deviceId: this.deviceId,
                status: this.selectedStatus,
                subStatusId: this.selectedSubStatus
            }).then(() => {
                // If status is Completed
                if (this.selectedStatus === "1") {
                    this.loading = true;
                    this.setDeviceReviewComplete({
                        jobId: this.jobId,
                        deviceId: this.deviceId
                    }).then(() => {
                        this.setDeviceComplete({
                            jobId: this.jobId,
                            deviceId: this.deviceId
                        }).then(() => {
                            this.loadDeviceStatus({
                                statusId: this.device.status_id
                            });
                            window.location.reload();
                        });
                    });
                    // If status is NOT Completed
                } else if (this.selectedStatus === "37") {
                    this.loading = true;
                    this.setDeviceReviewComplete({
                        jobId: this.jobId,
                        deviceId: this.deviceId
                    }).then(() => {
                        this.setDeviceRepairedPendingQC({
                            jobId: this.jobId,
                            deviceId: this.deviceId
                        }).then(() => {
                            this.loadDeviceStatus({
                                statusId: this.device.status_id
                            });
                            window.location.reload();
                        });
                    });
                    // If status is NOT Completed
                } else if (this.selectedStatus !== "1" && this.selectedStatus !== "37") {
                    this.setDeviceNotComplete({
                        jobId: this.jobId,
                        deviceId: this.deviceId
                    }).then(() => {
                        this.loading = false;
                        // @TODO - Remove for general SAW Request release
                        //  @TODO - Disabled at time of commit.
                        //   if (this.userService.details.id === 160 ||
                        //       this.userService.details.id === 201 ||
                        //       this.userService.details.id === 363 ||
                        //       this.userService.details.id === 855 ||
                        //       this.userService.details.id === 1125 ||
                        //       this.userService.details.id === 1276
                        //   ) {
                        //     if (this.selectedStatus === "22") {
                        //       // Go raise a SAW
                        //       return this.$router.push(`/saw/request/${this.deviceId}/job/${this.jobId}`);
                        //     }
                        //   }
                        this.loadDeviceStatus({
                            statusId: this.device.status_id
                        });

                        if (!this.revisitRequired) {
                            window.location.reload();
                        }
                    });
                }
            });
        },

        handleDeviceStatusSelectChange() {
            if (this.selectedStatus === '9' && this.deviceParts.length > 0) {
                this.$bvModal.show('remove-parts-modal');
            }

            // reset the selected sub status
            this.selectedSubStatus = null;

            this.loadDeviceSubStatuses({
                statusId: this.selectedStatus
            });
        }
    }
}
</script>

<style scoped>

</style>
